import { PermissionEnum } from '@Application/Security/Roles';
import FetchUserStatuses from '@Application/Users/UserStatuses/Queries/FetchUserStatuses';
import fetchUserStatuses from '@Application/Users/UserStatuses/fetchUserStatuses';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FacilityFilter from '@UI/Pages/Administration/Users/Filters/FacilityFilter';
import OrganizationFilter from '@UI/Pages/Administration/Users/Filters/OrganizationFilter';
import RoleFilter from '@UI/Pages/Administration/Users/Filters/RoleFilter';
import SearchFilter from '@UI/Pages/Administration/Users/Filters/SearchFilter';
import SortFilter from '@UI/Pages/Administration/Users/Filters/SortFilter';
import StatusFilter from '@UI/Pages/Administration/Users/Filters/StatusFilter';
import UserFilterView from '@UI/Pages/Administration/Users/Filters/UserFilterView';
import isUserStatusFilter from '@UI/Pages/Administration/Users/Filters/isUserStatusFilter';
import userFiltersSignal, {
	setUserFilters,
} from '@UI/Pages/Administration/Users/Filters/Signals/userFiltersSignal';
import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Props = {
	readonly roles: readonly RoleView[] | null;
	/** Every organization the caller is assigned to, each carrying its facilities — `null` while still
	 * loading (`useCurrentOrganization().organizations`, composed client-side). */
	readonly organizations: readonly ComposedOrganization[] | null;
};

/**
 * Writes into `userFiltersSignal`; never receives the current filter state as a prop, the field
 * components read the signal themselves (`guides/lists-and-filters.md`). The catalogs stay here so
 * opening the panel never reloads them. The facility dropdown's options follow the selected
 * organization: every facility when none is selected, only that organization's when one is.
 *
 * The facility dropdown itself only renders for a `USER_READ_ACCOUNT` holder: that permission is
 * what puts the caller on the account-wide route, the only one with a broad list worth narrowing —
 * everyone else is already on a single-facility route, where the filter would duplicate the URL.
 */
const Filters: React.FC<Props> = ({ roles, organizations }) => {
	const currentFilters = useSignalValue(userFiltersSignal);
	const canReadAccountUsers = useHasPermission(PermissionEnum.USER_READ_ACCOUNT);
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();
	const dispatch = useDispatch();

	// No `cache` preset: `stopOn` reads the store, which survives this component unmounting, so the
	// catalog is fetched once per session rather than once per mount.
	const { data: userStatuses } = useStoreQuery({
		selector: (state) => state.userStatusesStore.userStatuses,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchUserStatuses(new FetchUserStatuses())),
	});

	const handleReset = React.useCallback(() => {
		setUserFilters(UserFilterView.createDefault());
	}, []);

	const statusOptions = React.useMemo(
		() => (userStatuses ?? []).filter((entry) => isUserStatusFilter(entry.value)),
		[userStatuses],
	);

	const selectedOrganization = React.useMemo(
		() =>
			organizations?.find((organization) => organization.id === currentFilters.organizationId) ?? null,
		[organizations, currentFilters.organizationId],
	);

	const facilityOptions = React.useMemo(() => {
		if (selectedOrganization !== null) {
			return selectedOrganization.facilities;
		}

		return (organizations ?? []).flatMap((organization) => organization.facilities);
	}, [organizations, selectedOrganization]);

	return (
		<React.Fragment>
			<Grid columns={12} columnGap={1} rowGap={1} alignItems="end">
				<GridItem columnSize={{ xl: 4, lg: 4, md: 9, sm: 9, xs: 8 }}>
					<SearchFilter />
				</GridItem>
				<ConditionalLoader
					data={!isCompact}
					fallback={
						<GridItem columnSize={{ md: 3, sm: 3, xs: 4 }}>
							<FilterPanelTrigger
								panelId={panelId}
								isOpen={isOpen}
								activeCount={currentFilters.activeFilterCount}
								onClick={open}
							/>
						</GridItem>
					}
				>
					<GridItem columnSize={{ xl: 2, lg: 4, md: 3 }}>
						<OrganizationFilter organizations={organizations} />
					</GridItem>
					<ConditionalLoader data={canReadAccountUsers}>
						<GridItem columnSize={{ xl: 2, lg: 4, md: 3 }}>
							<FacilityFilter facilities={facilityOptions} loading={organizations === null} />
						</GridItem>
					</ConditionalLoader>
					<GridItem columnSize={{ xl: 2, lg: 4, md: 3 }}>
						<RoleFilter roles={roles} />
					</GridItem>
					<GridItem columnSize={{ xl: 2, lg: 4, md: 3 }}>
						<StatusFilter statuses={statusOptions} disabled={userStatuses === null} />
					</GridItem>
					<GridItem columnSize={{ xl: 2, lg: 4, md: 3 }}>
						<SortFilter />
					</GridItem>
				</ConditionalLoader>
			</Grid>
			<FilterPanelSidebar
				panelId={panelId}
				visible={isOpen}
				activeCount={currentFilters.activeFilterCount}
				onHide={close}
				onReset={handleReset}
			>
				<Grid columns={1} rowGap={1}>
					<GridItem>
						<OrganizationFilter organizations={organizations} withLabel />
					</GridItem>
					<ConditionalLoader data={canReadAccountUsers}>
						<GridItem>
							<FacilityFilter
								facilities={facilityOptions}
								loading={organizations === null}
								withLabel
							/>
						</GridItem>
					</ConditionalLoader>
					<GridItem>
						<RoleFilter roles={roles} withLabel />
					</GridItem>
					<GridItem>
						<StatusFilter statuses={statusOptions} disabled={userStatuses === null} withLabel />
					</GridItem>
					<GridItem>
						<SortFilter withLabel />
					</GridItem>
				</Grid>
			</FilterPanelSidebar>
		</React.Fragment>
	);
};

export default Filters;
